import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Loader2,
  RefreshCw,
  Store,
  MapPin,
  Tag,
  Calendar,
  Users,
  Building2,

  Landmark,
  Banknote,
  Boxes,
  TrendingUp,
  TrendingDown,
  Receipt,
  Percent,
  Home,
  Bolt,
  Car,
  HandCoins,
  UsersRound,
  Sprout,
} from "lucide-react";

type BusinessDetails = {
  id?: number | string;

  businessName?: any;
  businessTown?: any;
  businessType?: any;
  businessYearStarted?: any;
  businessNoEmployees?: any;
  businessBuldingName?: any;
  bldOwner?: any;
  businessStreet?: any;
  businessIndstry?: any;

  business_address?: any;
  businessLicenceNumber?: any;
  licenced?: any;
  businessAverageIncome?: any;
  currentstockvalue?: any;
  weeklysales?: any;
  weeklyspendstock?: any;
  weeklygrossprofit?: any;
  weeklyexpenses?: any;

  weeklynetprofit?: any;
  rentamount?: any;
  utilities?: any;
  transport?: any;
  weeklychamacontribution?: any;
  nochamamembers?: any;
  chamapayout?: any;
};

export default function CustomerBusinessTab({ customerId }: { customerId: string }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [b, setB] = useState<BusinessDetails | null>(null);

  function safe(v: any) {
    if (v === null || v === undefined) return "";
    const s = String(v).trim();
    if (!s || s === "0" || s.toLowerCase() === "null") return "";
    return s;
  }

  function nf(v: any) {
    const s = safe(v);
    if (!s) return "";
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString() : s;
  }

  function isYesLike(v: any) {
    const s = safe(v).toLowerCase();
    return ["yes", "true", "1", "ok", "good", "licensed", "licenced"].includes(s);
  }

  function YesNoBadge({ value }: { value: any }) {
    const s = safe(value);
    if (!s) return <span className="text-muted-foreground">—</span>;

    const ok = isYesLike(s);
    return (
      <Badge
        className={cn(
          "border px-2.5 py-1 rounded-full text-xs font-semibold",
          ok ? "bg-emerald-500/10 text-emerald-700 border-emerald-200" : "bg-muted text-foreground border"
        )}
      >
        {s}
      </Badge>
    );
  }

  function InfoLine({
    icon: Icon,
    label,
    value,
    formatNumber,
  }: {
    icon: any;
    label: string;
    value: any;
    formatNumber?: boolean;
  }) {
    const s = formatNumber ? nf(value) : safe(value);
    if (!s) return null;

    return (
      <div className="flex items-start gap-2">
        <div className="h-8 w-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
          <div className="text-sm font-semibold break-words">{s}</div>
        </div>
      </div>
    );
  }

  function Metric({
    icon: Icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: any;
  }) {
    const s = nf(value) || safe(value);
    if (!s) return null;

    return (
      <div className="rounded-xl border bg-muted/20 p-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
            <div className="text-sm font-semibold truncate">{s}</div>
          </div>
        </div>
      </div>
    );
  }

  async function load() {
    if (!customerId) return;

    setLoading(true);
    setErr("");

    try {
      // ✅ Make sure your Laravel route matches this:
      // Route::get('/customer-profile/{customerId}/business', ...)
      const res = await apiFetch<any>(`/customer-profile/${customerId}/business`);

      const payload = res?.data ?? res;
      // support either {data:{...}} or {...}
      const root = payload?.data ?? payload;

      setB(root as BusinessDetails);
    } catch (e: any) {
      setErr(e?.message || "Failed to load business details");
      setB(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const kpis = useMemo(() => {
    return {
      weeklysales: b?.weeklysales,
      weeklygrossprofit: b?.weeklygrossprofit,
      weeklynetprofit: b?.weeklynetprofit , // safety (typos happen)
      weeklyexpenses: b?.weeklyexpenses,
      currentstockvalue: b?.currentstockvalue,
      businessAverageIncome: b?.businessAverageIncome,
    };
  }, [b]);

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading business details...
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 space-y-3">
          <div className="text-sm text-destructive">{err}</div>
          <Button variant="secondary" onClick={load} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!b) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 text-sm text-muted-foreground">
          No business details found for this customer.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4">
      {/* 1) Business profile */}
      <Card className="shadow-card xl:col-span-4">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
              <Store className="h-4 w-4" />
              Business Profile
            </div>
            {safe(b.licenced) ? <YesNoBadge value={b.licenced} /> : <Badge className="bg-muted text-foreground border">Info</Badge>}
          </div>

          <div className="space-y-3">
            <InfoLine icon={Store} label="Business Name" value={b.businessName} />
            <InfoLine icon={MapPin} label="Location (Town)" value={b.businessTown} />
            <InfoLine icon={Tag} label="Type" value={b.businessType} />
            <InfoLine icon={Calendar} label="Started" value={b.businessYearStarted} />
            <InfoLine icon={Users} label="No. Employees" value={b.businessNoEmployees} />
            <InfoLine icon={Building2} label="Building Name" value={b.businessBuldingName} />
            <InfoLine icon={Home} label="Building Ownership" value={b.bldOwner} />
            <InfoLine icon={Building2} label="Street" value={b.businessStreet} />
            <InfoLine icon={Landmark} label="Industry" value={b.businessIndstry} />
          </div>
        </CardContent>
      </Card>

      {/* 2) Licensing & Address */}
      <Card className="shadow-card xl:col-span-4">
        <CardContent className="p-4 space-y-4">
          <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Licensing & Address
          </div>

          <div className="space-y-3">
            <InfoLine icon={MapPin} label="Business Address" value={b.business_address} />
            <InfoLine icon={Receipt} label="Licence Number" value={b.businessLicenceNumber} />

            {safe(b.licenced) ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border bg-muted/20 p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Receipt className="h-4 w-4 text-primary shrink-0" />
                  <div className="text-sm font-medium text-foreground/80 truncate">Business Licensed</div>
                </div>
                <YesNoBadge value={b.licenced} />
              </div>
            ) : null}

            <Metric icon={Banknote} label="Average Income" value={b.businessAverageIncome} />
            <Metric icon={Boxes} label="Current Stock Value" value={b.currentstockvalue} />
          </div>
        </CardContent>
      </Card>

      {/* 3) Weekly performance */}
      <Card className="shadow-card xl:col-span-4">
        <CardContent className="p-4 space-y-4">
          <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Weekly Performance
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Metric icon={TrendingUp} label="Weekly Sales" value={b.weeklysales} />
            <Metric icon={Sprout} label="Weekly Spend (Stock)" value={b.weeklyspendstock} />
            <Metric icon={Percent} label="Weekly Gross Profit" value={b.weeklygrossprofit} />
            <Metric icon={TrendingDown} label="Weekly Expenses" value={b.weeklyexpenses} />
            <Metric icon={Banknote} label="Weekly Net Profit" value={b.weeklynetprofit} />
          </div>

          <div className="mt-2 h-px bg-border/60" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Metric icon={Home} label="Rent Amount" value={b.rentamount} />
            <Metric icon={Bolt} label="Utilities" value={b.utilities} />
            <Metric icon={Car} label="Transport" value={b.transport} />
          </div>

          <div className="mt-2 h-px bg-border/60" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Metric icon={HandCoins} label="Weekly Chama Contribution" value={b.weeklychamacontribution} />
            <Metric icon={UsersRound} label="Chama Members" value={b.nochamamembers} />
            <Metric icon={Banknote} label="Chama Payout" value={b.chamapayout} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}